export const extractStartParam = (initData: string | undefined): string | null => {
  if (!initData) return null;

  const urlParams = new URLSearchParams(initData);
  return urlParams.get('start_param');
};

const getPrimaryParam = (startParam: string | null): string | null => {
  if (!startParam) return null;
  const idx = startParam.indexOf('--');
  return idx >= 0 ? startParam.substring(0, idx) : startParam;
};

const extractSegment = (startParam: string | null, prefix: string): string | null => {
  if (!startParam) return null;
  const segments = startParam.split('--');
  for (const seg of segments) {
    if (seg.startsWith(prefix)) {
      return seg.substring(prefix.length);
    }
  }
  return null;
};

export const extractReferralId = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('ref_')) {
    return null;
  }

  return primary.substring(4);
};

export const extractCaseSlug = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary?.startsWith('case_')) return null;

  const slug = primary.substring(5);
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? slug : null;
};

export const getCaseRoute = (startParam: string | null) => {
  const caseSlug = extractCaseSlug(startParam);
  return caseSlug
    ? ({ tab: 'cases', screen: 'case-details', params: { caseId: caseSlug } } as const)
    : null;
};

export const extractCaseOpeningId = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('co_')) {
    return null;
  }

  return primary.substring(3);
};

export const extractInventoryItemId = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('item_')) {
    return null;
  }

  return primary.substring(5);
};

export const extractCaseCategorySlug = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('cat_')) {
    return null;
  }

  return primary.substring(4) || null;
};

export const extractGiveawayId = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('giveaway_')) {
    return null;
  }

  return primary.substring(9);
};

export const extractPromocodeParam = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary) return null;

  if (primary === 'promocode') {
    return '';
  }

  if (primary.startsWith('promocode_')) {
    return primary.substring(10);
  }

  return null;
};

export const isProfileParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'profile';
};

export const isInventoryParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'inventory';
};

export const isWheelParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'wheel';
};

export const isReferralsParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'referrals';
};

export const isBandParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'band';
};

export const isCs2LinkParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'cs2_link';
};

export const isCs2MatchesParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'cs2_matches';
};

export const isUpgradeTopParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'upgrade_top';
};

export const getUpgradeTopRoute = (startParam: string | null) =>
  isUpgradeTopParam(startParam)
    ? ({ tab: 'games', screen: 'upgrade', params: { openTop: true } } as const)
    : null;

export const isReferralContestParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'refcontest';
};

export const isAchievementsParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'achievements';
};

export const isRushMidSurveyParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'rushmidsurvey';
};

export const extractPartyCode = (startParam: string | null): string | null => {
  const p = getPrimaryParam(startParam);
  return p && p.startsWith('party_') ? p.slice('party_'.length) : null;
};

export const extractGameId = (startParam: string | null): string | null => {
  const p = getPrimaryParam(startParam);
  return p && p.startsWith('game_') ? p.slice('game_'.length) : null;
};

export const extractReplayId = (startParam: string | null): string | null => {
  const p = getPrimaryParam(startParam);
  return p && p.startsWith('replay_') ? p.slice('replay_'.length) : null;
};

export const isRushMidParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'rushmid';
};

export const isRouletteParam = (startParam: string | null): boolean => {
  return getPrimaryParam(startParam) === 'roulette';
};

export const extractRouletteMatchId = (startParam: string | null): string | null => {
  const p = getPrimaryParam(startParam);
  return p && p.startsWith('roulette_') ? p.slice('roulette_'.length) : null;
};

export const extractCaseBattleId = (startParam: string | null): string | null => {
  const p = getPrimaryParam(startParam);
  return p && p.startsWith('battle_') ? p.slice('battle_'.length) : null;
};

export const isCaseBattlesParam = (startParam: string | null): boolean => getPrimaryParam(startParam) === 'battles';

export const extractUserId = (startParam: string | null): string | null => {
  const primary = getPrimaryParam(startParam);
  if (!primary || !primary.startsWith('profile_')) {
    return null;
  }

  return primary.substring(8);
};

export const extractYclid = (startParam: string | null): string | null =>
  extractSegment(startParam, 'yclid_');

export const extractCampaignId = (startParam: string | null): string | null =>
  extractSegment(startParam, 'cid_');

export const extractAdId = (startParam: string | null): string | null =>
  extractSegment(startParam, 'aid_');

export function getBotUrl(): string {
  const botUsername = 'cs_shot_bot';
  return `https://t.me/${botUsername}/app?startapp=website`;
}
